<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { InvoicesEmbed, NotificationType } from '@/Pejer/tables/Invoices'
import { differenceInDays } from 'date-fns'
import { MailCheck, MailX } from 'lucide-vue-next'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import { Button } from 'pejer/src/components/ui/button'
import { toast } from 'pejer/src/components/ui/toast'
import usePejerMutation from 'pejer/src/usePejerMutation'
import InitiateDebtCollectionButton from './InitiateDebtCollectionButton.vue'

defineProps<{
  invoice: InvoicesEmbed.ListItem
}>()

const notificationTypeToText: Record<NotificationType, string> = {
  PaymentReminder: 'Betalingspåminnelse sendt',
  OverdueNotice: 'Purring sendt',
  DebtCollectionNotice: 'Inkassovarsel er sendt',
  PaymentRequest: 'Inkassokrav sendt',
  EnforcementNotice: 'Varsel om rettslig inndrivelse sendt',
}

const pejer = usePejer()
const { mutate, dataState } = usePejerMutation(pejer.urls.invoices.startDebtCollection, {
  queryKeys: [['invoices'], ['payments']],
  onSuccess: () => {
    toast({
      title: 'Betalingsoppfølging stoppet',
      description: 'Vi har stoppet betalingsoppfølging på fakturaen.',
    })
  },
})

const daysSince = (dateString: string) => {
  return differenceInDays(new Date(), new Date(dateString))
}
</script>
<template>
  <div class="flex flex-col">
    <div
      v-if="invoice.paymentGateway !== 'checkout' && invoice.paymentGateway !== 'managedInvoice'"
    ></div>
    <div
      v-else-if="!invoice.canActivatePersonalDebtCollection"
      class="text-xs italic text-muted-foreground w-full text-wrap"
    >
      Onboardingen av bedriften er ikke fullført, ingen handling kan utføres.
    </div>
    <div
      v-else-if="
        (invoice.paymentStatus === 'InvoiceConfirmed' ||
          (invoice.paymentGateway === 'managedInvoice' &&
            invoice.paymentStatus === 'InitiatedPayment')) &&
        (!invoice.kidNorwayInvoice.debtCollectionActivated ||
          !invoice.hasApprovedPersonalDebtCollectionAgreement)
      "
      class="text-red-600"
    >
      <InitiateDebtCollectionButton :invoice="invoice">
        <Button variant="outline" class="hover:text-red-900">
          <MailCheck class="w-4 h-4 mr-2" />Følg opp ubetalt faktura
        </Button>
      </InitiateDebtCollectionButton>
    </div>
    <template v-else>
      <div
        class="text-green-600 flex flex-wrap items-center gap-x-2"
        :class="{
          'text-yellow-600': invoice.latestNotification?.notificationType === 'EnforcementNotice',
        }"
      >
        Betalingsoppfølging startet
        <DataStateButton
          :data-state="dataState"
          variant="outline"
          class="hover:text-red-900 text-yellow-600"
          @click="
            mutate({ pathArgs: { id: invoice.paymentReferenceID }, body: { enabled: false } })
          "
        >
          <MailX class="w-4 h-4 mr-2" />Stopp
        </DataStateButton>
      </div>
      <span
        v-if="invoice.latestNotification?.notificationType"
        class="text-slate-500 text-xs font-normal"
      >
        {{ notificationTypeToText[invoice.latestNotification?.notificationType] ?? 'Varsel sendt' }}
        <span v-if="invoice.latestNotification?.actionDueDate" class="italic text-slate-400">
          (Forfall
          {{ new Date(invoice.latestNotification?.actionDueDate).toLocaleDateString('no-NO') }})
        </span>
        <span v-else class="italic text-red-500">
          ({{ daysSince(invoice.latestNotification?.sentTime) }}
          {{ daysSince(invoice.latestNotification?.sentTime) === 1 ? 'dag' : 'dager' }}
          siden)
        </span>
      </span>
    </template>
  </div>
</template>
